import React, { useState, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import headLogo from '../../assets/blue_gold_head-32.png';
import './Sidebar.css';
import BetaPill from '../primitives/BetaPill';
import { Link } from 'react-router-dom';

/*
 * Sidebar (phase 0 scaffold)
 * Uses design tokens to prepare migration from header tabs.
 * Will eventually host primary navigation & collapsible sections.
 */
export function Sidebar({ children }) {
  const [hoveredItem, setHoveredItem] = useState(null);
  const [panelPos, setPanelPos] = useState({ top: 0, left: 0 });
  const sidebarRef = useRef(null);

  const handleItemHover = useCallback((item, element) => {
    if (!element) {
      setHoveredItem(null);
      return;
    }
    const itemRect = element.getBoundingClientRect();
    // Fixed-position the panel just right of the sidebar, vertically centered with the icon
    setPanelPos({
      top: itemRect.top + itemRect.height / 2,
      left: itemRect.right + 10,
    });
    setHoveredItem(item);
  }, []);

  const handleSidebarLeave = useCallback(() => {
    setHoveredItem(null);
  }, []);

  return (
    <>
    <nav
      data-tour="sidebar"
      data-tutorial="nav-sidebar"
      className="dh-sidebar dh-sidebar--iconOnly"
      aria-label="Primary"
      ref={sidebarRef}
      onMouseLeave={handleSidebarLeave}
    >
      <div className="dh-sidebar__brand">
        <img className="dh-sidebar__brand-icon" src={headLogo} alt="DocuHounds" />
        <BetaPill />
      </div>
      <div className="dh-sidebar__scroll">
        {React.Children.map(children, child =>
          React.isValidElement(child)
            ? React.cloneElement(child, { onItemHover: handleItemHover })
            : child
        )}
      </div>

    </nav>

      {/* Floating hover label panel — portalled to body to escape overflow clipping */}
      {ReactDOM.createPortal(
        <div
          className={`dh-sidebar__hover-panel${hoveredItem ? ' is-visible' : ''}`}
          style={{ top: panelPos.top, left: panelPos.left }}
          aria-hidden="true"
        >
          {hoveredItem && (
            <>
              <span className="dh-sidebar__hover-title">{hoveredItem.label}</span>
              {hoveredItem.description && (
                <span className="dh-sidebar__hover-desc">{hoveredItem.description}</span>
              )}
            </>
          )}
        </div>,
        document.body
      )}
    </>
  );
}

export function SidebarSection({ label, children, onItemHover }) {
  return (
    <section className="dh-sidebar__section">
      {label && <h2 className="dh-sidebar__section-label">{label}</h2>}
      <div className="dh-sidebar__section-items">
        {React.Children.map(children, child =>
          React.isValidElement(child)
            ? React.cloneElement(child, { onItemHover })
            : child
        )}
      </div>
    </section>
  );
}

export function SidebarItem({ icon, iconActive, active, children, href, ariaLabel, hoverLabel, hoverDescription, onItemHover }) {
  const itemRef = useRef(null);
  const hasLabel = !!children;
  const chosenIcon = active && iconActive ? iconActive : icon;

  const handleMouseEnter = () => {
    const label = hoverLabel || ariaLabel;
    if (label && onItemHover) {
      onItemHover({ label, description: hoverDescription }, itemRef.current);
    }
  };

  const handleMouseLeave = () => {
    // Let Sidebar's onMouseLeave handle clearing
  };

  const content = (
    <div
      className={"dh-sidebar__item" + (active ? ' is-active' : '')}
      ref={itemRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {chosenIcon && <span className="dh-sidebar__icon" aria-hidden>{chosenIcon}</span>}
      {hasLabel && <span className="dh-sidebar__label">{children}</span>}
    </div>
  );
  // Use react-router Link for internal navigation to avoid full page reloads
  return href ? (
    <Link to={href} aria-label={ariaLabel || (hasLabel ? String(children) : undefined)} className="dh-sidebar__link">{content}</Link>
  ) : content;
}
